// ============================================================================
// Logistics Board — the ops team's daily trip-planning surface, in our native
// terms. One Board renders ONE LANE — the half of the operation it was given:
// FIRST MILE (supplier pickups, WH↔WH transfers) or LAST MILE (shipments,
// dropships, returns). Outstation (intercity part-load) splits across the two by
// LEG, so both boards carry an "Outstation" section holding their own half.
// Operations mounts one of each, side by side as tabs; the lane is a server-side
// scope, so counts, the attention strip and the section panels below all agree —
// except under an active SEARCH, which deliberately reaches across both boards.
//
// Within a lane, bookings are grouped into board SECTIONS (escalations /
// to-assign / on a truck / outstation / returns / held …), and within each, sub-grouped by TRIP
// (vehicle + driver) or by the ops person they're ASSIGNED TO. ESCALATIONS come
// first and outrank the working sections: a truck somebody has raised an
// escalation on leaves to-assign / on-a-truck entirely and is worked from there
// (carrying what it was flagged for) until someone resolves it. A date picker
// scopes to the target dispatch day (today by default); an attention strip on
// top surfaces what needs a human, and each row carries its attention + SLA
// overlay. Polls every ~25s (paused while a drawer / modal is open).
//
// This is the stateful CONTAINER; the pieces live alongside it: `board_controls`
// (strip + filters), `board_columns` (the table columns + row-action confirms),
// and `board_grouping` (sub-grouping + option constants).
// ============================================================================
import { Alert, AutoComplete, Button, Collapse, Empty, Input, Modal,Select, Space, Table, Tag, Tooltip, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "../index.module.scss";
import { BoardLane, BoardSection, LogisticsOrderRow, LogisticsSummary } from "../types";
import { fmtIST } from "../format";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import {
  assignOwnerByIds,
  autoAssignOwnersByIds,
  fetchMissingOrders,
  getLogisticsSummary,
  listLogisticsOrders,
  listTeamMembers,
  listWarehouses,
  MissingOrderRow,
  resolveLogisticsIssue,
  resolveStageIssue,
  TeamMember,
  WarehouseOption,
} from "./logistics.api";
import { buildMissingColumns } from "./board_missing";
import AllocationItemsModal from "./components/allocation_items_modal";
import AssignTripModal from "./components/assign_trip_modal";
import BookingDrawer from "./components/booking_drawer";
import EditLogisticsModal from "./components/edit_logistics_modal";
import IssueDrawer from "./components/issue_drawer";
import { CrmIssueDrawer } from "../components/crm_issue_thread";
import ReassignModal from "./components/reassign_modal";
import { SECTION_META, SECTION_ORDER } from "./components/board_meta";
import BoardControls, { UNASSIGNED_OWNER } from "./board_controls";
import { buildColumns } from "./board_columns";
import { groupRows } from "./board_grouping";
import DelhiveryBookingModal from "./delhivery_booking_modal";
import DelhiveryBulkModal from "./delhivery_bulk_modal";
import DelhiveryMultistopModal from "./delhivery_multistop_modal";
import { useCanManageDelhivery } from "../../../custom_hooks/use_delhivery_access";
import { DELHIVERY_CANCEL_REASONS, delhiveryCancelOrder } from "../../../apis/po.api";

// Section cargo weight (kg) → a compact label. Rounded to whole kg with Indian grouping; null when
// there's nothing to show (absent/zero), so the header renders no weight rather than "0 kg".
const fmtSectionWeight = (kg?: number | null): string | null =>
  typeof kg === "number" && kg > 0 ? `${Math.round(kg).toLocaleString("en-IN")} kg` : null;

// `fixedCity` pins the delivery-city scope for a board that IS one city cut — the Outstation client
// tab is the board locked to `city=outstation`. Its dropdown is hidden and Clear keeps it.
// `excludeOutstation` is the other side of that split: Last Mile drops those same trucks, so each is
// worked from one tab (picking "Outstation client" in its city filter therefore shows nothing).
// No `lane` = BOTH halves on one board (the Outstation Client tab: an outstation customer's pickup
// and delivery legs worked together). Sections both halves share are then split per half, so a
// first-mile escalation never merges with a last-mile one; `sectionLabels` names them per half.
const LANE_NEUTRAL_SECTIONS = new Set<BoardSection>(["escalation", "held", "outstation", "other", "closed"]);
const LANE_TITLE: Record<BoardLane, string> = { first_mile: "First Mile", last_mile: "Last Mile" };

const Board: React.FC<{
  lane?: BoardLane;
  fixedCity?: string;
  excludeOutstation?: boolean;
  // Drop return (reverse-leg) bookings. Last Mile hands returns to the shared Returns tab so a
  // single truck doesn't surface in two places.
  excludeReturns?: boolean;
  sectionLabels?: Partial<Record<BoardLane, Partial<Record<BoardSection, string>>>>;
  // Section keys to render ahead of the usual order (a split section's key is `${lane}_${section}`).
  leadSections?: string[];
  // Lead with the MISSING ORDERS section — deliveries promised on or before the picked day that never
  // got a truck. Last Mile only: that is the board whose team owns the customer's promise, and a row
  // here is by definition absent from every other section (it has no booking to be in one).
  showMissingOrders?: boolean;
  // SEARCH-ONLY board — what Operations' Search tab mounts. The board, its sections and its tables,
  // but it fetches NOTHING until something is typed. A plain board load is the most expensive query
  // this screen has (unpaginated, a whole lane, the reason polling had to be removed), and ops arrive
  // holding a PO number far more often than they arrive to read it — so paying for a lane nobody read
  // was the wrong default. No `lane` with it on purpose: a search already reaches across both boards
  // (the server drops the lane scope), which is the point — an outstation order has legs on either
  // side and nobody can know in advance which tab holds the one they want.
  searchOnly?: boolean;
  // Days past the picked date the board also reaches into. The picked day stays the control; the
  // dispatch upper bound becomes `date + dispatchWindowDays`. The Outstation Client tab sets 15 —
  // intercity bookings are planned well ahead, so its team works the coming fortnight, not one day.
  dispatchWindowDays?: number;
}> = ({
  lane,
  fixedCity,
  excludeOutstation,
  excludeReturns,
  sectionLabels,
  leadSections,
  showMissingOrders,
  searchOnly,
  dispatchWindowDays = 0,
}) => {
  const [rows, setRows] = useState<LogisticsOrderRow[]>([]);
  const [summary, setSummary] = useState<LogisticsSummary | null>(null);
  // Per lane+section cargo weight (kg), keyed `${lane}_${section}`. Kept in its own state so the
  // section headers show weight even if the response carries it top-level, without a summary object.
  const [combinedWeight, setCombinedWeight] = useState<Record<string, number>>();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const [date, setDate] = useState<Dayjs>(dayjs()); // show bookings dispatching ON OR BEFORE this date
  const [provider, setProvider] = useState("");
  const [warehouse, setWarehouse] = useState<number | undefined>(undefined); // scope to one MD warehouse
  const [city, setCity] = useState<string | undefined>(fixedCity); // scope to one delivery city
  const [assignee, setAssignee] = useState("");
  const [attention, setAttention] = useState("");
  const [sla, setSla] = useState("");
  const [expressOnly, setExpressOnly] = useState(false);
  // Intercity cut: trucks whose goods cross warehouses (a TRANSFER leg somewhere in the allotment's
  // plan). Those moves are one hop of a longer chain, so ops works them as their own set.
  const [transferOnly, setTransferOnly] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("none");
  // FIRST MILE only: narrow to one PICKUP POINT (the vendor's godown), so an operator working a
  // supplier on the phone sees only that supplier's trucks. Client-side over the rows already
  // loaded — the board fetches its whole lane unpaginated, and the server has no vendor scope — so
  // it costs no extra query. The attention strip above stays LANE-wide on purpose: it is the board's
  // workload, not this vendor's.
  const [vendor, setVendor] = useState<string | undefined>(undefined);

  // MISSING ORDERS — promised on or before the picked day, no truck ever created. Its own fetch
  // because the board's list is rooted on bookings and these rows have none; it is cheap and bounded
  // server-side (see `oms/logistics/services/missing_orders.py`), and it rides the same filters.
  const [missingRows, setMissingRows] = useState<MissingOrderRow[]>([]);
  const [missingEscalateFor, setMissingEscalateFor] = useState<MissingOrderRow | null>(null);
  // The contents modal, opened by clicking a Missing Orders / Awaiting Payment row. Its own state
  // because that modal is fed from the ALLOTMENT here, not from a booking — these rows have none.
  const [missingItemsFor, setMissingItemsFor] = useState<MissingOrderRow | null>(null);
  const [missingResolveFor, setMissingResolveFor] = useState<MissingOrderRow | null>(null);
  const [missingResolveNote, setMissingResolveNote] = useState("");
  const [missingResolving, setMissingResolving] = useState(false);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]); // ops team for the Owner dropdown
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]); // MD warehouses for the filter
  const [viewing, setViewing] = useState<LogisticsOrderRow | null>(null);
  const [itemsFor, setItemsFor] = useState<LogisticsOrderRow | null>(null); // contents modal (row click)
  const [editing, setEditing] = useState<LogisticsOrderRow | null>(null);
  const [reassigning, setReassigning] = useState<LogisticsOrderRow | null>(null);
  const [issueFor, setIssueFor] = useState<LogisticsOrderRow | null>(null); // escalation drawer
  const [notesFor, setNotesFor] = useState<LogisticsOrderRow | null>(null); // read-only notes drawer
  // Closing an escalation out — the way a truck leaves the escalation section. Its own prompt (not a
  // bare confirm) because the close-out note goes into the operation log beside the raise.
  const [resolveFor, setResolveFor] = useState<LogisticsOrderRow | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);
  // Who may close an escalation out: the `oms.update` permission — the same slug the server's
  // resolve endpoint requires, held by admin and the delivery and procurement teams. The board
  // itself is a wider audience (`oms.logistics` reaches manager too), so reading an escalation and
  // closing it are separate rights. Server-resolved, so the role matrix stays editable in one place
  // (Branch → permissions) instead of being hard-coded here.
  const { hasPermission, loaded: permsLoaded } = usePermissions();
  const canResolveEscalation = permsLoaded && hasPermission("oms.update");
  // Every other write on the board follows the endpoint behind it: status transitions, the Owner
  // dropdown (a PATCH), edit / reassign / raise escalation / retry and Assign trip vehicle are all
  // default OMS writes (`oms.update`); the bulk owner tools hit the two `oms.logistics` endpoints.
  // A read-only role (e.g. sales, `oms.read` only) still sees the board, just none of its buttons.
  // False while loading, so nothing flashes for someone who may not have it.
  const canUpdate = canResolveEscalation;
  const canAssignOwners = permsLoaded && hasPermission("oms.logistics");
  // Driver & vehicle column — its own slug (default admin), separate from the board's read access.
  const canViewDriverVehicle = permsLoaded && hasPermission("oms.driver_vehicle");
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false); // row-selection mode for bulk assignment
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [manualAssigning, setManualAssigning] = useState(false);
  const [assigneePick, setAssigneePick] = useState(""); // "assign to a person" — chosen owner id
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Delhivery (admin + delivery-team roles). Single modal keyed by one po_number; bulk modal by a list.
  // Selection is shared across the board's many per-section/group tables (rowKey `id` is unique board-wide).
  const canDelhivery = useCanManageDelhivery();
  const [delhiveryPo, setDelhiveryPo] = useState<string | null>(null);
  const [bulkPos, setBulkPos] = useState<string[] | null>(null);
  // Multistop: book all selected POs as ONE Delhivery order (one rider, many pickups → one drop),
  // instead of the per-truck bookings the bulk modal makes.
  const [multistopPos, setMultistopPos] = useState<string[] | null>(null);
  const [selectedRows, setSelectedRows] = useState<LogisticsOrderRow[]>([]);

  // The single-row Delhivery button: a truck with one PO opens the single modal; a consolidated truck
  // (multiple POs) opens the bulk modal seeded with just those POs.
  const onBookDelhivery = useCallback((r: LogisticsOrderRow) => {
    const pos = r.po_numbers ?? [];
    if (pos.length <= 1) setDelhiveryPo(pos[0] ?? null);
    else setBulkPos(pos);
  }, []);

  // Cancel-Delhivery prompt: the PO to cancel, the (required) reason, and the in-flight flag.
  const [cancelPo, setCancelPo] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const onCancelDelhivery = useCallback((r: LogisticsOrderRow) => {
    setCancelReason("");
    setCancelPo(r.po_numbers?.[0] ?? null);
  }, []);

  const runCancelDelhivery = async () => {
    if (!cancelPo || !cancelReason.trim()) return;
    setCancelling(true);
    const res = await delhiveryCancelOrder(cancelPo, cancelReason.trim());
    setCancelling(false);
    if (res.ok) {
      message.success("Delhivery booking cancelled — this PO can be booked again");
      setCancelPo(null);
      load({ silent: true });
    } else {
      message.error(res.error || "Could not cancel this Delhivery booking");
    }
  };

  const openResolve = useCallback(
    (r: LogisticsOrderRow) => {
      // Belt and braces: both callers are already hidden without the permission, so this only
      // matters if a third one is ever added without one.
      if (!canResolveEscalation) return;
      setResolveNote("");
      setResolveFor(r);
    },
    [canResolveEscalation]
  );

  const runResolve = async () => {
    if (!resolveFor) return;
    setResolving(true);
    const updated = await resolveLogisticsIssue(resolveFor.id, resolveNote);
    setResolving(false);
    if (updated) {
      setResolveFor(null);
      // The row's SECTION changes on resolve (escalation → its working section), so merging the
      // returned row in place is enough — no refetch needed for it to move.
      onSaved(updated);
    }
  };

  const overlayOpen = !!(viewing || itemsFor || editing || reassigning || assignOpen);
  const overlayRef = useRef(overlayOpen);
  overlayRef.current = overlayOpen;

  // Type-to-search debounce.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const scope = useMemo(
    () => ({
      // Single date → the board shows everything due to dispatch ON OR BEFORE it (no lower bound),
      // pushed `dispatchWindowDays` further out when the tab works a forward window.
      dispatch_to: date.add(dispatchWindowDays, "day").format("YYYY-MM-DD"),
      // This board is one half of the operation. The server honours the scope on a plain load (the
      // other half is never fetched) but DROPS it while searching, so a lookup finds an order on
      // whichever board owns it — see the note under the count line.
      board_lane: lane,

      provider_type: provider || undefined,
      warehouse_id: warehouse,
      city,
      exclude_outstation: excludeOutstation || undefined,
      exclude_returns: excludeReturns || undefined,
      // The Owner filter is tri-state: "" = any, a user id = that owner, or the UNASSIGNED sentinel
      // = only trucks with no owner (sent as `unassigned`, never as an assigned_to id).
      assigned_to: assignee && assignee !== UNASSIGNED_OWNER ? assignee.trim() : undefined,
      unassigned: assignee === UNASSIGNED_OWNER || undefined,
      express: expressOnly || undefined,
      transfer: transferOnly || undefined,
      include_cancelled: showCancelled || undefined,
    }),
    [
      lane,
      excludeOutstation,
      excludeReturns,
      date,
      dispatchWindowDays,
      provider,
      warehouse,
      city,
      assignee,
      expressOnly,
      transferOnly,
      showCancelled,
    ]
  );

  // A search-only board with an empty box: there is no question to answer yet, so no query is issued.
  const searchIdle = !!searchOnly && !search;

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (searchIdle) {
        setRows([]);
        setCount(0);
        setSummary(null);
        setUpdatedAt(null);
        setLoading(false);
        return;
      }
      if (!opts.silent) setLoading(true);
      // The list response CARRIES the attention strip (folded from the rows the server already
      // materialised), so the board's poll costs one pass through the board query instead of two —
      // that second pass was half the load on a query heavy enough to saturate the database.
      // A search is the one case the server can't fold it into: the search widens the list's scope
      // (no date bound, cancelled + delivered surfaced), so the counts wouldn't describe the board.
      // Only there do we still ask for the strip separately — in parallel, as before.
      const [list, standaloneSummary] = await Promise.all([
        listLogisticsOrders({
          ...scope,
          // A search must surface the hit no matter its dispatch date — drop the (today-by-default)
          // date bound while searching, so it isn't silently filtered out by the day scope. The
          // board LANE is dropped by the server on the same principle (an outstation order has legs
          // on both boards, so an operator cannot know which tab to search from); it is left in the
          // params here on purpose, so the un-searched board keeps its half-the-rows cut.
          ...(search ? { dispatch_to: undefined } : {}),
          attention: attention || undefined,
          sla: sla || undefined,
          search: search || undefined,
          // No limit: the board is a work list read top to bottom, and a truncated page hides
          // deliveries with nothing on screen to say so. The server bounds the set instead
          // (delivered / cancelled / superseded excluded, dispatch-date window, one lane).
        }),
        search ? getLogisticsSummary(scope) : Promise.resolve(undefined),
      ]);
      const sum = standaloneSummary ?? list?.summary;
      // combined_weight may arrive nested in `summary` OR at the top level of the list response —
      // read it from either, independent of whether a summary object came back.
      const weight =
        sum?.combined_weight ?? list?.combined_weight ?? standaloneSummary?.combined_weight;
      if (list) {
        setRows(list.results);
        setCount(list.count);
        setError(false);
        setUpdatedAt(Date.now());
      } else {
        setError(true);
      }
      if (sum) setSummary(sum);
      if (weight) setCombinedWeight(weight);
      if (!opts.silent) setLoading(false);
    },
    [scope, attention, sla, search, searchIdle]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Does the Missing Orders section apply under the filters currently set?
  //
  // Four of the board's filters are properties OF A BOOKING — the carrier, the ops owner, the
  // attention bucket and the SLA verdict. A Missing Orders row has no booking, so it cannot satisfy any of
  // them, and showing it anyway would mean the section ignored the control the operator just used.
  // Hiding it is the honest answer: they have narrowed to trucks, and these are not trucks.
  //
  // The rest DO apply and are honoured — the day picker, warehouse, delivery city, express and the
  // search run server-side; the intercity cut runs client-side off `is_transfer_chain`, the same flag
  // the board's own Intercity pill reads. `showCancelled` is additive (it brings cancelled trucks
  // back) and so is left alone: nothing about it contradicts a delivery that was never booked.
  const missingApplicable =
    !!showMissingOrders && !searchIdle && !provider && !assignee && !attention && !sla;

  const loadMissing = useCallback(async () => {
    if (!missingApplicable) {
      setMissingRows([]);
      return;
    }
    const data = await fetchMissingOrders({
      // The board shows bookings due to dispatch ON OR BEFORE the picked day; this shows promises on
      // or before the same day. A search drops the date bound here for the same reason it does on the
      // board — a lookup must surface its hit whatever day it was promised for.
      on_date: search ? undefined : date.format("YYYY-MM-DD"),
      fulfilment_warehouse_id: warehouse,
      city,
      express: expressOnly,
      search: search || undefined,
    });
    setMissingRows(data?.results ?? []);
  }, [missingApplicable, date, warehouse, city, expressOnly, search]);

  useEffect(() => {
    loadMissing();
  }, [loadMissing]);

  // The intercity cut, applied here rather than server-side: it is the one honourable filter the
  // missing-orders endpoint has no parameter for, and `is_transfer_chain` already rides every row.
  const visibleMissingRows = useMemo(
    () => (transferOnly ? missingRows.filter((r) => r.is_transfer_chain) : missingRows),
    [missingRows, transferOnly]
  );

  // AWAITING PAYMENT is split out of Missing Orders and shown at the BOTTOM.
  //
  // An order gated shut on the customer's money is not a missing order in any sense this board means:
  // nothing is stuck, nothing is late in our hands, and the delivery team owes it no action — the leg
  // releases itself the moment the payment lands. Leaving these mixed in did two bad things at once:
  // it blamed fulfilment for a collections matter, and it padded the one list whose entire value is
  // that every row on it is a promise we really did drop. So they go below everything, where they
  // read as "known, waiting, not ours", and Missing Orders keeps its edge.
  //
  // The split is on the server's `payment_pending` code, which is decided from the real payment gate
  // (`missing_orders.is_payment_hold`) — never from the hold's wording.
  const [trulyMissingRows, awaitingPaymentRows] = useMemo(() => {
    const missing: MissingOrderRow[] = [];
    const awaiting: MissingOrderRow[] = [];
    for (const r of visibleMissingRows) {
      (r.reason_code === "payment_pending" ? awaiting : missing).push(r);
    }
    return [missing, awaiting];
  }, [visibleMissingRows]);

  // Ops team + MD warehouses for the filters — fetched once (they change rarely, no need to poll).
  useEffect(() => {
    listTeamMembers().then(setTeamMembers);
    listWarehouses().then(setWarehouses);
  }, []);

  // No auto-refresh. The board query is expensive enough that a per-minute poll from every open
  // board was, on its own, enough to saturate the production database — each operator's tab kept
  // issuing work faster than Postgres could retire it, and the queue never drained. The refresh
  // button in the controls (with the "updated HH:mm:ss IST" stamp beside it) is the way to pull
  // fresh rows; every action on the board still reloads it on completion, so the view is never
  // stale after something the operator actually did. Restore polling only once the board's query
  // is paginated in SQL.

  const onSaved = (updated: LogisticsOrderRow) =>
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));

  const filtersApplied = !!(
    provider ||
    warehouse ||
    (city && !fixedCity) ||
    assignee ||
    attention ||
    sla ||
    search ||
    expressOnly ||
    transferOnly ||
    showCancelled ||
    vendor
  );

  const clearFilters = () => {
    setProvider("");
    setWarehouse(undefined);
    setCity(fixedCity);
    setAssignee("");
    setAttention("");
    setSla("");
    setExpressOnly(false);
    setTransferOnly(false);
    setShowCancelled(false);
    setVendor(undefined);
    setSearchInput("");
    setSearch("");
  };

  const columnHandlers = useMemo(
    () => ({
      busyKey,
      setBusyKey,
      onSaved,
      reload: () => load({ silent: true }),
      onView: setViewing,
      onEdit: setEditing,
      onReassign: setReassigning,
      onRaiseIssue: setIssueFor,
      onResolveIssue: openResolve,
      canResolveEscalation,
      canUpdate,
      canViewDriverVehicle,
      onViewNotes: setNotesFor,
      teamMembers,
      isAdmin: canDelhivery === true,
      onBookDelhivery,
      onCancelDelhivery,
    }),
    [
      busyKey,
      load,
      teamMembers,
      canDelhivery,
      onBookDelhivery,
      onCancelDelhivery,
      openResolve,
      canResolveEscalation,
      canUpdate,
      canViewDriverVehicle,
    ]
  );

  const columns = useMemo(() => buildColumns(columnHandlers), [columnHandlers]);
  // The escalation section's tables get one extra column — WHAT each truck was flagged for. Built as
  // a second set rather than a column that renders empty everywhere else, so no other section pays
  // 220px for a field that is null on all of its rows.
  const escalationColumns = useMemo(
    () => buildColumns({ ...columnHandlers, showEscalation: true }),
    [columnHandlers]
  );

  // Missing Orders' columns — the board's own cells (see `board_missing`), minus the four that are
  // booking-only. The escalation column mounts only when something on the section is actually
  // flagged, the same rule `showEscalation` follows above.
  const missingHandlers = useMemo(
    () => ({
      onRaiseIssue: setMissingEscalateFor,
      onResolveIssue: (r: MissingOrderRow) => {
        setMissingResolveNote("");
        setMissingResolveFor(r);
      },
      canUpdate,
      canResolveEscalation,
    }),
    [canUpdate, canResolveEscalation]
  );

  // One column set per section, differing only in whether the escalation column is mounted — each
  // section answers that for ITS OWN rows, so neither pays 220px for a field the other populated.
  const missingColumns = useMemo(
    () =>
      buildMissingColumns({
        ...missingHandlers,
        showEscalation: trulyMissingRows.some((r) => r.escalation),
      }),
    [missingHandlers, trulyMissingRows]
  );
  const awaitingPaymentColumns = useMemo(
    () =>
      buildMissingColumns({
        ...missingHandlers,
        showEscalation: awaitingPaymentRows.some((r) => r.escalation),
      }),
    [missingHandlers, awaitingPaymentRows]
  );

  // Merge rather than refetch: a reload would re-sort the section under the operator who just raised
  // the escalation, and the row they were working would move out from under them.
  const mergeMissingEscalation = (stageId: number, escalation: MissingOrderRow["escalation"]) =>
    setMissingRows((prev) =>
      prev.map((r) => {
        if (r.id !== stageId) return r;
        // BOTH fields, in step. The ESCALATED tag reads `active_escalations`; merging only
        // `escalation` would leave a just-raised escalation invisible on the row until the next
        // refetch — and an operator who cannot see their own raise lands up raising it twice.
        // Only this row's own (delivery-leg) entry changes; upstream ones are another team's and are
        // left exactly as the server reported them.
        const upstream = (r.active_escalations ?? []).filter((e) => !e.is_delivery_leg);
        const own = escalation
          ? [
              {
                operation_type: r.operation_type,
                issue_type: escalation.issue_type ?? null,
                count: escalation.count ?? 1,
                raised_by: escalation.raised_by ?? null,
                raised_at: escalation.raised_at ?? null,
                is_delivery_leg: true,
              },
            ]
          : [];
        // Server order is oldest leg first, and the delivery leg is last by sequence.
        return { ...r, escalation, active_escalations: [...upstream, ...own] };
      })
    );

  const runMissingResolve = async () => {
    if (!missingResolveFor) return;
    setMissingResolving(true);
    const ok = await resolveStageIssue(missingResolveFor.id, missingResolveNote);
    setMissingResolving(false);
    if (ok) {
      mergeMissingEscalation(missingResolveFor.id, null);
      setMissingResolveFor(null);
    }
  };

  // The pickup points on the board right now, for the Vendor filter — derived from the loaded rows
  // rather than the vendor master, so the list is only ever the suppliers this lane actually has
  // trucks standing at today.
  const vendorOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) {
      const name = r.origin_label?.trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo(
    () => (vendor ? rows.filter((r) => r.origin_label?.trim() === vendor) : rows),
    [rows, vendor]
  );

  // Rows partitioned into sections, in the fixed display order (only non-empty ones shown). On a
  // both-halves board a lane-neutral section is split per half — Last Mile's first, then First Mile's.
  const sectionsWithRows = useMemo(() => {
    const splitByLane = (s: BoardSection) => !lane && LANE_NEUTRAL_SECTIONS.has(s);
    const byKey = new Map<string, { section: BoardSection; lane: BoardLane; rows: LogisticsOrderRow[] }>();
    for (const r of visibleRows) {
      const s = r.board_section;
      const key = splitByLane(s) ? `${r.board_lane}_${s}` : s;
      if (!byKey.has(key)) byKey.set(key, { section: s, lane: lane ?? r.board_lane, rows: [] });
      byKey.get(key)!.rows.push(r);
    }
    const order = SECTION_ORDER.flatMap((s) =>
      splitByLane(s) ? [`last_mile_${s}`, `first_mile_${s}`] : [s]
    );
    const lead = (leadSections ?? []).filter((k) => order.includes(k));
    return [...lead, ...order.filter((k) => !lead.includes(k))]
      .filter((key) => byKey.get(key)?.rows.length)
      .map((key) => ({ key, ...byKey.get(key)! }));
  }, [visibleRows, lane, leadSections]);

  const sectionLabel = (section: BoardSection, rowLane: BoardLane) =>
    sectionLabels?.[rowLane]?.[section] ??
    (!lane && LANE_NEUTRAL_SECTIONS.has(section)
      ? `${SECTION_META[section].label} · ${LANE_TITLE[rowLane]}`
      : SECTION_META[section].label);

  const rowClassName = (r: LogisticsOrderRow) =>
    r.sla_state === "breached" ? styles.boardRowBreached ?? "" : "";

  // PO numbers across every ticked truck, de-duped and order-preserving.
  const selectedPoNumbers = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of selectedRows)
      for (const po of r.po_numbers ?? [])
        if (po && !seen.has(po)) {
          seen.add(po);
          out.push(po);
        }
    return out;
  }, [selectedRows]);

  // Shared selection across the board's many tables: each table replaces only its own contribution.
  const rowSelectionFor = (tableRows: LogisticsOrderRow[]) => ({
    selectedRowKeys: selectedRows.map((r) => r.id),
    getCheckboxProps: (record: LogisticsOrderRow) => ({
      disabled: !record.is_delhivery_eligible || !!record.is_delhivery_booked,
    }),
    onChange: (_keys: React.Key[], rowsInTable: LogisticsOrderRow[]) => {
      const tableIds = new Set(tableRows.map((r) => r.id));
      setSelectedRows((prev) => [
        ...prev.filter((r) => !tableIds.has(r.id)),
        ...rowsInTable,
      ]);
    },
  });

  // Row-selection for bulk assignment: checkboxes on every table (any internal user), into the same
  // shared `selectedRows`. Every row is selectable — including already-owned ones, so "assign to a
  // person" can reassign them (auto-assign will still skip the ones that already have an owner).
  const bulkSelectionFor = (tableRows: LogisticsOrderRow[]) => ({
    selectedRowKeys: selectedRows.map((r) => r.id),
    onChange: (_keys: React.Key[], rowsInTable: LogisticsOrderRow[]) => {
      const tableIds = new Set(tableRows.map((r) => r.id));
      setSelectedRows((prev) => [...prev.filter((r) => !tableIds.has(r.id)), ...rowsInTable]);
    },
  });

  // Enter/leave selection mode; always start clean so a stale tick / owner pick can't carry over
  // (the same `selectedRows` also backs the admin Delhivery bar).
  const toggleSelectMode = () => {
    setSelectedRows([]);
    setAssigneePick("");
    setSelectMode((on) => !on);
  };

  const runAutoAssignSelected = async () => {
    const ids = selectedRows.map((r) => r.id);
    if (ids.length === 0) return;
    setAutoAssigning(true);
    const res = await autoAssignOwnersByIds(ids);
    setAutoAssigning(false);
    if (res) {
      setSelectedRows([]);
      setSelectMode(false);
      load();
    }
  };

  // "Assign to a person" — set the one chosen owner on every ticked booking (overrides existing).
  const runAssignToPerson = async () => {
    const ids = selectedRows.map((r) => r.id);
    if (ids.length === 0 || !assigneePick) return;
    setManualAssigning(true);
    const res = await assignOwnerByIds(ids, assigneePick);
    setManualAssigning(false);
    if (res) {
      setSelectedRows([]);
      setAssigneePick("");
      setSelectMode(false);
      load();
    }
  };

  return (
    <>
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load the latest board"
          description="What you see below may be out of date. Check your connection and try again."
          action={
            <Button size="small" danger onClick={() => load()}>
              Try again
            </Button>
          }
        />
      )}

      <BoardControls
        summary={summary}
        attention={attention}
        setAttention={setAttention}
        sla={sla}
        setSla={setSla}
        date={date}
        setDate={setDate}
        provider={provider}
        setProvider={setProvider}
        warehouse={warehouse}
        setWarehouse={setWarehouse}
        warehouses={warehouses}
        city={city}
        setCity={fixedCity ? undefined : setCity}
        assignee={assignee}
        setAssignee={setAssignee}
        teamMembers={teamMembers}
        expressOnly={expressOnly}
        setExpressOnly={setExpressOnly}
        transferOnly={transferOnly}
        setTransferOnly={setTransferOnly}
        showCancelled={showCancelled}
        setShowCancelled={setShowCancelled}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        vendor={vendor}
        // Changing the cut drops the ticked rows: they are about to leave the screen, and the bulk
        // bars (assign / Delhivery) must never act on trucks the operator can no longer see.
        setVendor={
          lane === "first_mile"
            ? (v) => {
                setSelectedRows([]);
                setVendor(v);
              }
            : undefined
        }
        vendorOptions={vendorOptions}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        // Land the cursor in the box the operator came here to type in.
        autoFocusSearch={searchOnly}
        filtersApplied={filtersApplied}
        clearFilters={clearFilters}
        // Refresh pulls BOTH lists. They are separate fetches (bookings vs legs with no booking), and
        // a Refresh that quietly left one of them stale would be worse than no button.
        onRefresh={() => {
          load();
          loadMissing();
        }}
        loading={loading}
        onAssignTrip={() => setAssignOpen(true)}
        canAssignTrip={canUpdate}
        selectMode={selectMode}
        onToggleSelectMode={toggleSelectMode}
        canAutoAssign={canAssignOwners}
      />

      <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
        {searchIdle
          ? "Type a carrier ref, ENQ or PO number above to find a booking"
          : count === 0
            ? search
              ? "Nothing matches this search"
              : "No open bookings for this date / filter"
            : `${count} booking${count === 1 ? "" : "s"}`}
        {/* The vendor cut is applied here, not by the server, so say which slice is on screen. */}
        {vendor && (
          <span style={{ marginLeft: 8 }}>
            · showing {visibleRows.length} at {vendor}
          </span>
        )}
        {search && (
          <span style={{ marginLeft: 8 }}>
            · searching First Mile and Last Mile, any date or status
          </span>
        )}
        {updatedAt && <span style={{ marginLeft: 8 }}>· updated {fmtIST(updatedAt, "HH:mm:ss")} IST</span>}
      </div>

      {/* Selection bar — two ways to assign the ticked bookings:
          1) Auto-assign owners → route each to its routing-table POC (fills blank owners only);
          2) Assign to a person → set one chosen owner on all of them (overrides existing). */}
      {selectMode && canAssignOwners && (
        <Space style={{ marginBottom: 8 }} wrap>
          <Button
            type="primary"
            loading={autoAssigning}
            disabled={selectedRows.length === 0}
            onClick={runAutoAssignSelected}
          >
            Auto-assign owners ({selectedRows.length})
          </Button>
          <span style={{ color: "#8c8c8c" }}>or</span>
          <Select
            placeholder="Assign to…"
            showSearch
            style={{ width: 180 }}
            value={assigneePick || undefined}
            onChange={(v) => setAssigneePick(v ?? "")}
            options={teamMembers.map((m) => ({ value: m.value, label: m.label }))}
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button
            loading={manualAssigning}
            disabled={selectedRows.length === 0 || !assigneePick}
            onClick={runAssignToPerson}
          >
            Assign to person ({selectedRows.length})
          </Button>
          <Button onClick={() => setSelectedRows([])} disabled={selectedRows.length === 0}>
            Clear
          </Button>
        </Space>
      )}

      {/* Bulk Delhivery bar — admin-only, shown once eligible rows are ticked. POs are collected across
          all selected trucks (a consolidated truck contributes several), de-duped. */}
      {!selectMode && canDelhivery && selectedRows.length > 0 && (
    <Space style={{ marginBottom: 8 }} wrap>
      <Button type="primary" onClick={() => setBulkPos(selectedPoNumbers)}>
        Bulk book Delhivery ({selectedPoNumbers.length} PO
        {selectedPoNumbers.length === 1 ? "" : "s"})
      </Button>
      {/* Book the whole selection as ONE Delhivery order (multistop: one rider, many pickups → one
          customer drop). Needs at least 2 POs. */}
      <Tooltip
        title={
          selectedPoNumbers.length < 2
            ? "Select at least 2 POs to book a single multistop pickup"
            : undefined
        }
      >
        <Button
          disabled={selectedPoNumbers.length < 2}
          onClick={() => setMultistopPos(selectedPoNumbers)}
        >
          Book as single Delhivery ({selectedPoNumbers.length} PO
          {selectedPoNumbers.length === 1 ? "" : "s"})
        </Button>
      </Tooltip>
      <Button onClick={() => setSelectedRows([])}>Deselect all</Button>
    </Space>
  )
}

{
  sectionsWithRows.length === 0 && visibleMissingRows.length === 0 ? (
    // `visibleMissingRows` (not `trulyMissingRows`) on purpose: an awaiting-payment row is still
    // something on screen, so the board is not empty just because nothing is actually missing.
    <Empty
      description={
        loading
          ? "Loading…"
          : searchIdle
            ? "Search for a booking to begin — First Mile and Last Mile together, any date or status."
            : search
              ? "No booking on either board matches this search."
              : "Nothing open on the board for this date / filter."
      }
    />
  ) : (
  <Collapse
    defaultActiveKey={[
      ...(trulyMissingRows.length ? ["missing"] : []),
      ...sectionsWithRows.map((s) => s.key),
      ...(awaitingPaymentRows.length ? ["awaiting_payment"] : []),
    ]}
    ghost
  >
    {/* MISSING ORDERS leads the board, ahead of escalations, for the same reason escalations lead the
        working sections: it is the set nothing else on the page can show you. An escalated truck is
        at least visible somewhere; a delivery with no truck is visible NOWHERE else, so if it does
        not come first it does not get seen. Rendered as a panel of this same Collapse, by the same
        table, so it reads as part of the board rather than a thing bolted on top of it. */}
    {trulyMissingRows.length > 0 && (
      <Collapse.Panel
        key="missing"
        header={
          <Space>
            <Tooltip title="Promised to the customer on or before the selected day, with no truck ever created for the last leg — so they are on no other section of this board. Each row names the leg that is actually blocking it.">
              <strong style={{ color: "#cf1322" }}>Missing Orders</strong>
            </Tooltip>
            <Tag color="error">{trulyMissingRows.length}</Tag>
          </Space>
        }
      >
        <Table
          rowKey="id"
          size="small"
          tableLayout="fixed"
          columns={missingColumns}
          dataSource={trulyMissingRows}
          pagination={false}
          scroll={{ x: 1700 }}
          // Clicking a row opens the SAME contents modal every other section opens — fed from the
          // allotment rather than a truck, because there is no truck (see `AllocationCargoView`).
          onRow={(r) => ({
            onClick: () => setMissingItemsFor(r),
            style: { cursor: "pointer" },
          })}
          // Still no row SELECTION: the bulk bars (owner assign, Delhivery) act on booking ids.
        />
      </Collapse.Panel>
    )}
    {sectionsWithRows.map(({ key, section, lane: rowLane, rows: sectionRows }) => (
      <Collapse.Panel
        key={key}
        header={
          <Space>
            <Tooltip title={SECTION_META[section].help}>
              <strong>{sectionLabel(section, rowLane)}</strong>
            </Tooltip>
            <Tag>{sectionRows.length}</Tag>
            {/* Total cargo weight for this lane+section, from the combined_weight map. */}
            {(() => {
              const w = fmtSectionWeight(combinedWeight?.[`${rowLane}_${section}`]);
              return w ? (
                <span style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>{w}</span>
              ) : null;
            })()}
          </Space>
        }
      >
        {groupRows(sectionRows, groupBy).map((g) => (
          <div key={g.key} style={{ marginBottom: groupBy === "none" ? 0 : 14 }}>
            {groupBy !== "none" && (
              <div style={{ fontSize: 13, fontWeight: 500, margin: "2px 0 6px", color: "#555" }}>
                {g.label} <span style={{ color: "#aaa", fontWeight: 400 }}>· {g.rows.length}</span>
              </div>
            )}
            <Table
              rowKey="id"
              size="small"
              tableLayout="fixed"
              columns={section === "escalation" ? escalationColumns : columns}
              dataSource={g.rows}
              pagination={false}
              rowClassName={rowClassName}
              rowSelection={
                selectMode && canAssignOwners
                  ? bulkSelectionFor(g.rows)
                  : canDelhivery
                    ? rowSelectionFor(g.rows)
                    : undefined
              }
              scroll={{ x: section === "escalation" ? 2000 : 1780 }}
              onRow={(r) => ({
                onClick: () => setItemsFor(r),
                style: { cursor: "pointer" },
              })}
            />
          </div>
        ))}
      </Collapse.Panel>
    ))}
    {/* AWAITING PAYMENT, last. Same rows, same columns, same table as Missing Orders above — these are
        also deliveries past their promise with no truck. What is different is WHOSE problem it is:
        the leg is gated shut on purpose until the money lands, so nothing is stuck, nothing is late in
        our hands, and the delivery team owes it no action. Keeping them in Missing Orders blamed
        fulfilment for a collections matter and padded the one list that has to stay all-signal. At the
        bottom, and in neutral colours, they read as "known, waiting, not ours". */}
    {awaitingPaymentRows.length > 0 && (
      <Collapse.Panel
        key="awaiting_payment"
        header={
          <Space>
            <Tooltip title="Promised on or before the selected day with no truck — but held deliberately because the order is not fully paid. Not a missing order: the leg releases itself the moment the payment lands or is approved, so nothing here needs a dispatch decision.">
              <strong>Awaiting Payment</strong>
            </Tooltip>
            <Tag>{awaitingPaymentRows.length}</Tag>
            <span style={{ fontSize: 12, color: "#8c8c8c", fontWeight: 400 }}>
              not missing — gated on the customer's payment
            </span>
          </Space>
        }
      >
        <Table
          rowKey="id"
          size="small"
          tableLayout="fixed"
          columns={awaitingPaymentColumns}
          dataSource={awaitingPaymentRows}
          pagination={false}
          scroll={{ x: 1700 }}
          onRow={(r) => ({
            onClick: () => setMissingItemsFor(r),
            style: { cursor: "pointer" },
          })}
          // No row SELECTION, as above: the bulk bars act on booking ids.
        />
      </Collapse.Panel>
    )}
  </Collapse>
)
}

      {/* The same modal, fed from the allotment — for the two leg-rooted sections whose rows have no
          booking. A separate instance rather than a shared one so neither source has to be nulled to
          open the other. */}
      <AllocationItemsModal
        booking={null}
        allocation={
          missingItemsFor?.allocation_id
            ? { id: missingItemsFor.allocation_id, label: missingItemsFor.po_number }
            : null
        }
        onClose={() => setMissingItemsFor(null)}
        // No truck, so nothing here can be split off one.
        hideShipSeparately
      />

      <AllocationItemsModal
        booking={itemsFor}
        onClose={() => setItemsFor(null)}
        // A PO moved to another transfer: it now has a truck of its own and this one may have been
        // cancelled, so the board has to re-read rather than wait for the next poll.
        onChanged={() => load({ silent: true })}
      />

      <BookingDrawer
        booking={viewing}
        onClose={() => setViewing(null)}
        onEdit={(r) => {
          setViewing(null);
          setEditing(r);
        }}
        onReassign={(r) => {
          setViewing(null);
          setReassigning(r);
        }}
      />
      <EditLogisticsModal
        open={!!editing}
        order={editing}
        teamMembers={teamMembers}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
      <ReassignModal
        open={!!reassigning}
        order={reassigning}
        onClose={() => setReassigning(null)}
        onReassigned={() => load()}
      />
      <IssueDrawer
        target={issueFor ? { kind: "booking", row: issueFor } : null}
        onClose={() => setIssueFor(null)}
        onRaised={() => load({ silent: true })}
      />
      {/* The same drawer, keyed on the delivery LEG — a Missing Orders row has no booking to key on, which
          is the whole reason it is in that section. */}
      <IssueDrawer
        target={missingEscalateFor ? { kind: "missing", row: missingEscalateFor } : null}
        onClose={() => setMissingEscalateFor(null)}
        onRaised={(escalation) => {
          if (!missingEscalateFor) return;
          // A raise whose response carried no escalation (a 502) is still a raise — re-read rather
          // than merge a null, or the row would sit there looking un-flagged and the next operator
          // would escalate the same order again.
          if (escalation) mergeMissingEscalation(missingEscalateFor.id, escalation);
          else loadMissing();
        }}
      />
      {notesFor?.sales_order_id && (
        <CrmIssueDrawer
          salesOrderId={notesFor.sales_order_id}
          open={!!notesFor}
          onClose={() => setNotesFor(null)}
        />
      )}
      <AssignTripModal open={assignOpen} onClose={() => setAssignOpen(false)} onAssigned={() => load()} />

{/* Delhivery — admin-only. Single-PO booking/status, and the bulk flow. */ }
{
  delhiveryPo && (
    <DelhiveryBookingModal
      poNumber={delhiveryPo}
      open={!!delhiveryPo}
      onClose={() => setDelhiveryPo(null)}
    />
  )
}
{
  bulkPos && (
    <DelhiveryBulkModal
      poNumbers={bulkPos}
      open={!!bulkPos}
      onClose={() => setBulkPos(null)}
      onBooked={() => setSelectedRows([])}
    />
  )
}
{
  multistopPos && (
    <DelhiveryMultistopModal
      poNumbers={multistopPos}
      open={!!multistopPos}
      onClose={() => setMultistopPos(null)}
      onBooked={() => {
        setMultistopPos(null);
        setSelectedRows([]);
        load();
      }}
    />
  )
}

      <Modal
        title={`Resolve escalation · booking #${resolveFor?.id ?? ""}`}
        open={!!resolveFor}
        onCancel={() => setResolveFor(null)}
        okText="Resolve escalation"
        okButtonProps={{ loading: resolving }}
        cancelText="Keep it open"
        onOk={runResolve}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
          {resolveFor?.escalation?.issue_type}
          {resolveFor?.escalation?.remarks ? ` — “${resolveFor.escalation.remarks}”` : ""}
        </div>
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          This takes the booking out of the escalation section and back into its normal queue. The
          escalation stays in the operation log — nothing is erased.
        </div>

        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={resolveNote}
          onChange={(e) => setResolveNote(e.target.value)}
          placeholder="How was it settled? (optional — goes into the log)"
        />
      </Modal>

      <Modal
        title={`Resolve escalation · ${missingResolveFor?.po_number || missingResolveFor?.lead_id || ""}`}
        open={!!missingResolveFor}
        onCancel={() => setMissingResolveFor(null)}
        okText="Resolve escalation"
        okButtonProps={{ loading: missingResolving }}
        cancelText="Keep it open"
        onOk={runMissingResolve}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
          {missingResolveFor?.escalation?.issue_type}
          {missingResolveFor?.escalation?.remarks
            ? ` — “${missingResolveFor.escalation.remarks}”`
            : ""}
        </div>
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          {/* Unlike a booking, resolving does NOT move this row anywhere: the delivery is still owed
              and still has no truck, so it stays in Missing Orders until one exists. Say so, or an
              operator will resolve it expecting the row to clear and escalate it again when it
              doesn't. */}
          This only clears the flag. The delivery is still owed, so the row stays in Missing Orders
          until a truck is created for it — and the escalation stays in the operation log.
        </div>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          value={missingResolveNote}
          onChange={(e) => setMissingResolveNote(e.target.value)}
          placeholder="How was it settled? (optional — goes into the log)"
        />
      </Modal>

      <Modal
        title={`Cancel Delhivery booking · ${cancelPo ?? ""}`}
        open={!!cancelPo}
        onCancel={() => setCancelPo(null)}
        okText="Cancel booking"
        okButtonProps={{ danger: true, disabled: !cancelReason.trim(), loading: cancelling }}
        cancelText="Keep booking"
        onOk={runCancelDelhivery}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
          Pick a reason or type your own — it's required and sent to Delhivery. After cancelling, the
          PO becomes bookable again.
        </div>
        <AutoComplete
          style={{ width: "100%" }}
          value={cancelReason}
          onChange={setCancelReason}
          placeholder="Reason for cancellation"
          options={DELHIVERY_CANCEL_REASONS.map((r) => ({ value: r }))}
          filterOption={(input, option) =>
            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>
    </>
  );
};

export default Board;
